/**
 * Merci : le « Lire la suite » du bloc Texte. Voir
 * `snippets/merci-text-toggle.liquid`.
 *
 * Le texte est replié par le CSS dès le premier rendu, sans attendre ce
 * script : pas de saut de mise en page. Le script mesure s'il dépasse, montre
 * le bouton dans ce cas seulement, et bascule l'état ouvert.
 *
 * **La mesure suit la largeur** (`ResizeObserver`) : un texte qui tient en
 * bureau peut dépasser en mobile, et inversement.
 *
 * **Un lien replié qui prend le focus ouvre le texte.** Sans cela, la
 * navigation au clavier entrerait dans la partie masquée, et le focus
 * deviendrait invisible.
 */

class MerciTextToggle extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #cible = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    const id = this.#button?.getAttribute('aria-controls');
    this.#cible = id ? document.getElementById(id) : null;
    if (!this.#button || !this.#cible) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#button.addEventListener('click', () => this.#basculer(!this.#ouvert()), { signal });
    this.#cible.addEventListener(
      'focusin',
      () => {
        if (!this.#ouvert()) this.#basculer(true);
      },
      { signal }
    );

    // Une première mesure tout de suite, sans attendre l'observateur : il ne
    // se déclenche qu'au rendu suivant, et jamais dans un onglet en arrière-plan.
    this.#mesurer();
    this.#observer = new ResizeObserver(() => this.#mesurer());
    this.#observer.observe(this.#cible);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #ouvert() {
    return this.#cible?.hasAttribute('data-merci-ouvert') ?? false;
  }

  /** Montre le bouton si le texte replié dépasse ses lignes. */
  #mesurer() {
    const cible = this.#cible;
    if (!cible || this.#ouvert()) return;

    this.hidden = cible.scrollHeight <= cible.clientHeight + 1;
  }

  /** @param {boolean} ouvrir */
  #basculer(ouvrir) {
    const cible = this.#cible;
    const button = this.#button;
    if (!cible || !button) return;

    cible.toggleAttribute('data-merci-ouvert', ouvrir);
    button.setAttribute('aria-expanded', String(ouvrir));

    const moins = button.dataset.labelLess ?? '';
    if (ouvrir) {
      // Sans libellé pour replier, l'ouverture est définitive.
      if (moins === '') this.hidden = true;
      else button.textContent = moins;
      return;
    }

    button.textContent = button.dataset.labelMore ?? '';
    this.#mesurer();

    // Replier un long texte fait remonter le bouton loin au-dessus de la
    // fenêtre : on ramène le début du texte en vue.
    if (cible.getBoundingClientRect().top < 0) {
      cible.scrollIntoView({ block: 'start' });
    }
  }
}

if (!customElements.get('merci-text-toggle')) {
  customElements.define('merci-text-toggle', MerciTextToggle);
}
